import type { DemoState } from '../state';

export const DEFAULT_CANONICAL_HISTORY_MAX = 50;

export type CanonicalSnapshot = DemoState;

export type CanonicalHistoryOptions = {
  maxEntries?: number;
};

export type CanonicalHistory<T extends { regionUndoStack?: unknown[] } = CanonicalSnapshot> = {
  baseline: T;
  undoStack: T[];
  redoStack: T[];
  maxEntries: number;
};

export type CanonicalHistoryStep<T extends { regionUndoStack?: unknown[] }> = {
  history: CanonicalHistory<T>;
  snapshot: T;
};

export function createCanonicalHistory<T extends { regionUndoStack?: unknown[] }>(
  baseline: T,
  options: CanonicalHistoryOptions = {},
): CanonicalHistory<T> {
  return {
    baseline: cloneCanonicalSnapshot(baseline),
    undoStack: [],
    redoStack: [],
    maxEntries: normaliseMaxEntries(options.maxEntries),
  };
}

export function resetCanonicalHistory<T extends { regionUndoStack?: unknown[] }>(
  history: CanonicalHistory<T>,
  baseline: T,
): CanonicalHistory<T> {
  return {
    ...history,
    baseline: cloneCanonicalSnapshot(baseline),
    undoStack: [],
    redoStack: [],
  };
}

export function clearCanonicalHistory<T extends { regionUndoStack?: unknown[] }>(
  history: CanonicalHistory<T>,
): CanonicalHistory<T> {
  return {
    ...history,
    undoStack: [],
    redoStack: [],
  };
}

export function canUndoCanonicalHistory<T extends { regionUndoStack?: unknown[] }>(
  history: CanonicalHistory<T>,
): boolean {
  return history.undoStack.length > 0;
}

export function canRedoCanonicalHistory<T extends { regionUndoStack?: unknown[] }>(
  history: CanonicalHistory<T>,
): boolean {
  return history.redoStack.length > 0;
}

export function pushCanonicalSnapshot<T extends { regionUndoStack?: unknown[] }>(
  history: CanonicalHistory<T>,
  snapshot: T,
): CanonicalHistory<T> {
  return {
    ...history,
    undoStack: capStack([...history.undoStack, cloneCanonicalSnapshot(snapshot)], history.maxEntries),
    redoStack: [],
  };
}

export function pushCanonicalBeforeMutation<T extends { regionUndoStack?: unknown[] }>(
  history: CanonicalHistory<T>,
  current: T,
): CanonicalHistory<T> {
  return pushCanonicalSnapshot(history, current);
}

export function commitCanonicalTransaction<T extends { regionUndoStack?: unknown[] }>(
  history: CanonicalHistory<T>,
  before: T,
  _after: T,
): CanonicalHistory<T> {
  return pushCanonicalSnapshot(history, before);
}

export function pushCanonicalAfterMutation<T extends { regionUndoStack?: unknown[] }>(
  history: CanonicalHistory<T>,
  before: T,
  after: T,
): CanonicalHistory<T> {
  return commitCanonicalTransaction(history, before, after);
}

export function undoCanonicalHistory<T extends { regionUndoStack?: unknown[] }>(
  history: CanonicalHistory<T>,
  current: T,
): CanonicalHistoryStep<T> {
  const snapshot = history.undoStack[history.undoStack.length - 1];
  if (snapshot === undefined) {
    return { history, snapshot: cloneCanonicalSnapshot(current) };
  }

  return {
    snapshot: cloneCanonicalSnapshot(snapshot),
    history: {
      ...history,
      undoStack: history.undoStack.slice(0, -1),
      redoStack: capStack([...history.redoStack, cloneCanonicalSnapshot(current)], history.maxEntries),
    },
  };
}

export function redoCanonicalHistory<T extends { regionUndoStack?: unknown[] }>(
  history: CanonicalHistory<T>,
  current: T,
): CanonicalHistoryStep<T> {
  const snapshot = history.redoStack[history.redoStack.length - 1];
  if (snapshot === undefined) {
    return { history, snapshot: cloneCanonicalSnapshot(current) };
  }

  return {
    snapshot: cloneCanonicalSnapshot(snapshot),
    history: {
      ...history,
      undoStack: capStack([...history.undoStack, cloneCanonicalSnapshot(current)], history.maxEntries),
      redoStack: history.redoStack.slice(0, -1),
    },
  };
}

export function cloneCanonicalSnapshot<T extends { regionUndoStack?: unknown[] }>(
  snapshot: T,
): T {
  return cloneValue(snapshot, true) as T;
}

function normaliseMaxEntries(maxEntries: number | undefined): number {
  if (maxEntries === undefined) return DEFAULT_CANONICAL_HISTORY_MAX;
  if (!Number.isFinite(maxEntries)) return DEFAULT_CANONICAL_HISTORY_MAX;
  return Math.max(1, Math.floor(maxEntries));
}

function capStack<T>(stack: T[], maxEntries: number): T[] {
  if (stack.length <= maxEntries) return stack;
  return stack.slice(stack.length - maxEntries);
}

function cloneValue(value: unknown, isRoot = false): unknown {
  if (value === null || typeof value !== 'object') return value;

  if (typeof ImageData !== 'undefined' && value instanceof ImageData) {
    return new ImageData(new Uint8ClampedArray(value.data), value.width, value.height);
  }

  if (value instanceof Uint8Array) return new Uint8Array(value);
  if (value instanceof Uint8ClampedArray) return new Uint8ClampedArray(value);
  if (value instanceof Uint16Array) return new Uint16Array(value);
  if (value instanceof Uint32Array) return new Uint32Array(value);
  if (value instanceof Int8Array) return new Int8Array(value);
  if (value instanceof Int16Array) return new Int16Array(value);
  if (value instanceof Int32Array) return new Int32Array(value);
  if (value instanceof Float32Array) return new Float32Array(value);
  if (value instanceof Float64Array) return new Float64Array(value);
  if (value instanceof BigInt64Array) return new BigInt64Array(value);
  if (value instanceof BigUint64Array) return new BigUint64Array(value);
  if (value instanceof DataView) {
    return new DataView(value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength));
  }
  if (value instanceof ArrayBuffer) return value.slice(0);
  if (value instanceof Date) return new Date(value.getTime());

  if (value instanceof Map) {
    const out = new Map<unknown, unknown>();
    for (const [key, entry] of value) {
      out.set(cloneValue(key), cloneValue(entry));
    }
    return out;
  }

  if (value instanceof Set) {
    const out = new Set<unknown>();
    for (const entry of value) {
      out.add(cloneValue(entry));
    }
    return out;
  }

  if (Array.isArray(value)) {
    return value.map((entry) => cloneValue(entry));
  }

  const out: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value)) {
    out[key] = isRoot && key === 'regionUndoStack' ? [] : cloneValue(entry);
  }
  return out;
}
